import { PRICE_WATCH_INLINE_KEY } from "@/lib/newsletter-popup-frequency"

/**
 * The write half of the "one price intent, one ask" contract.
 *
 * WHAT WAS WRONG (FRIDGE-279): `lib/newsletter-popup-frequency.ts` declares
 * `PRICE_WATCH_INLINE_KEY` and `components/newsletter-capture-popup.tsx` READS
 * it — the PDP popup variant is suppressed for anyone who already used the
 * inline widget. Nothing ever wrote it. There is no `localStorage` call
 * anywhere under `components/price-watch/`, so the read was against a key that
 * could only ever be absent and the suppression never fired once. A visitor
 * who handed over an email in the on-page form was still asked for it a second
 * time by the overlay. Asking twice for something already given is the exact
 * shape of a dark pattern this card exists to remove.
 *
 * The key is imported from the module that declares it, never re-typed here:
 * a literal copy is how the two halves drift apart again.
 *
 * Storage is passed in rather than read off `window` so the contract is
 * testable in the node test environment, and every failure mode is swallowed —
 * Safari private mode and "block all cookies" both throw on `setItem`, and a
 * suppression hint is never worth breaking a successful subscribe over.
 */
export function markInlineWatchUsed(
  storage: Pick<Storage, "setItem"> | null | undefined,
  now: number = Date.now()
): boolean {
  if (!storage) return false
  try {
    storage.setItem(PRICE_WATCH_INLINE_KEY, String(now))
    return true
  } catch {
    return false
  }
}

/** The browser's storage, or null when it is unavailable or blocked. */
export function browserStorage(): Pick<Storage, "setItem"> | null {
  try {
    if (typeof window === "undefined") return null
    return window.localStorage ?? null
  } catch {
    return null
  }
}
